import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { AdminAccelerationStatus, IAccelerationTaskInfo } from '~src/dataAcceleration/models/IAccelerationTaskInfo';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { selectSpaceInfo, selectSpaceInfoItem } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { isSiteTasksSpace } from '~src/spacesPlatform/state/SpaceInfo';
import { currentUserIsOwner } from '~src/spacesPlatform/utils/MixedContentHelpers';

import { IViewAcceleration, ViewDataAccelerationState } from '../types/IViewAcceleration';

export function getAccelerationStatusForViewSpace(viewAcceleration: IViewAcceleration): string {
  switch (viewAcceleration.accelerationState) {
  case ViewDataAccelerationState.Failed:
  case ViewDataAccelerationState.AccelerationNotUseful:
    return FeatureFlags.enhancedAccelerationTooltip ?
            Messages.accelerationStatusInactive() :
            Messages.accelerationStatusError();
  case ViewDataAccelerationState.Off:
    return Messages.accelerationStatusDisabled();
  case ViewDataAccelerationState.On:
    return Messages.accelerationStatusEnabled();
  case ViewDataAccelerationState.Pending:
  case ViewDataAccelerationState.SkippedDueToResourceConstraint:
    return Messages.accelerationStatusPending();
  case ViewDataAccelerationState.Recommended:
    return Messages.adminAccelerationStatusRecommended();
  case ViewDataAccelerationState.AutoAccelerated:
    return Messages.adminAccelerationStatusAutoAccelerated();
  case ViewDataAccelerationState.AdminSuspended:
    return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.adminAccelerationStatusSuspended() :
          Messages.accelerationStatusDisabled();
  case ViewDataAccelerationState.SystemSuspended:
    return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.adminAccelerationStatusSystemSuspended() :
          Messages.accelerationStatusDisabled();
  default:
    return Messages.accelerationStatusUnknown();
  }
}

export function getAccelerationStatusTooltipContentForViewSpace(viewAcceleration: IViewAcceleration): string {
  switch (viewAcceleration.accelerationState) {
  case ViewDataAccelerationState.Failed:
    return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.accelerationStatusErrorTooltip() :
          Messages.accelerationStatusError();
  case ViewDataAccelerationState.AccelerationNotUseful:
    return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.accelerationStatusNotUsefulTooltip() :
          Messages.accelerationStatusError();
  case ViewDataAccelerationState.Off:
    return Messages.accelerationStatusDisabled();
  case ViewDataAccelerationState.On:
    return Messages.accelerationStatusEnabled();
  case ViewDataAccelerationState.Pending:
    return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.accelerationStatusPendingTooltip() :
          Messages.accelerationStatusPending();
  case ViewDataAccelerationState.SkippedDueToResourceConstraint:
    return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.accelerationStatusSkippedTooltip() :
          Messages.accelerationStatusPending();
  case ViewDataAccelerationState.Recommended:
    return Messages.adminAccelerationStatusRecommended();
  case ViewDataAccelerationState.AutoAccelerated:
    return Messages.adminAccelerationStatusAutoAccelerated();
  case ViewDataAccelerationState.AdminSuspended:
    return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.adminAccelerationStatusSuspended() :
          Messages.accelerationStatusDisabled();
  case ViewDataAccelerationState.SystemSuspended:
    return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.adminAccelerationStatusSystemSuspended() :
          Messages.accelerationStatusDisabled();
  default:
    return Messages.accelerationStatusUnknown();
  }
}

export function getAdminAccelerationStatusForTaskPage(accelerationStatus?: string): string  {
  switch (accelerationStatus) {
    case AdminAccelerationStatus.SUSPENDED:
      return Messages.adminAccelerationStatusSuspended();
    case AdminAccelerationStatus.ACTIVE:
      return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.accelerationStatusEnabled() :
          Messages.adminAccelerationStatusActive();
    case AdminAccelerationStatus.PENDING:
    case AdminAccelerationStatus.SKIPPED_DUE_TO_RESOURCE_CONSTRAINT:
      return Messages.adminAccelerationStatusPending();
    case AdminAccelerationStatus.ERROR:
    case AdminAccelerationStatus.ACCELERATION_NOT_USEFUL :
      return FeatureFlags.enhancedAccelerationTooltip ?
          Messages.adminAccelerationStatusInactive() :
          Messages.adminAccelerationStatusError();
    case AdminAccelerationStatus.UNKNOWN:
      return Messages.adminAccelerationStatusUnknown();
    case AdminAccelerationStatus.SYSTEM_SUSPENDED:
      return Messages.adminAccelerationStatusSystemSuspended();
    case AdminAccelerationStatus.RECOMMENDED:
      return Messages.adminAccelerationStatusRecommended();
    case AdminAccelerationStatus.AUTO_ACCELERATED:
      return Messages.adminAccelerationStatusAutoAccelerated();
    default: return '';
  }
}

export function getAdminAccelerationStatusTooltipContentForTaskPage(accelerationStatus?: string): string  {
  switch (accelerationStatus) {
    case AdminAccelerationStatus.SUSPENDED:
      return Messages.adminAccelerationStatusSuspended();
    case AdminAccelerationStatus.ACTIVE:
      return Messages.adminAccelerationStatusActive();
    case AdminAccelerationStatus.RECOMMENDED:
      return Messages.adminAccelerationStatusRecommended();
    case AdminAccelerationStatus.AUTO_ACCELERATED:
      return Messages.adminAccelerationStatusAutoAccelerated();
    case AdminAccelerationStatus.PENDING:
      return FeatureFlags.enhancedAccelerationTooltip ?
              Messages.adminAccelerationStatusPendingTooltip() :
              Messages.adminAccelerationStatusPending();
    case AdminAccelerationStatus.ERROR:
      return FeatureFlags.enhancedAccelerationTooltip ?
              Messages.adminAccelerationStatusErrorTooltip() :
              Messages.adminAccelerationStatusError();
    case AdminAccelerationStatus.UNKNOWN:
      return Messages.adminAccelerationStatusUnknown();
    case AdminAccelerationStatus.SYSTEM_SUSPENDED:
      return Messages.adminAccelerationStatusSystemSuspended();
    case AdminAccelerationStatus.ACCELERATION_NOT_USEFUL:
      return FeatureFlags.enhancedAccelerationTooltip ?
             Messages.adminAccelerationStatusNotUsefulTooltip() :
             Messages.adminAccelerationStatusError();
    case AdminAccelerationStatus.SKIPPED_DUE_TO_RESOURCE_CONSTRAINT:
      return FeatureFlags.enhancedAccelerationTooltip ?
              Messages.adminAccelerationStatusSkippedTooltip() :
              Messages.adminAccelerationStatusPending();
    default: return '';
  }
}

const NUM_SECONDS_PER_MINUTE = 60;

// the following functions are used to find the numbers for the columns whose units may change
export function findLastMonthJobAvgTime(task: IAccelerationTaskInfo): number {
  // lastMonthJobAvgTime needs to be in minutes
  return task.lastMonthJobAvgTimeInSec / NUM_SECONDS_PER_MINUTE;
}

export function findEstimatedDiskUsage(task: IAccelerationTaskInfo): number {
  return task.estimatedDiskUsageKb;
}

export function showAccelerationStatusInViewSubspace(): boolean {
    if (!FeatureFlags.workbookAccelerationUIEnabled) {
        return false;
    }
    const state = getAppStore().getState();
    const spaceInfo = selectSpaceInfo(state);
    if (spaceInfo.subspaceName !== 'views') {
        return false;
    }

    if (getSessionManagementServiceInstance().isSessionSiteOrServerAdmin()) {
        return true;
    }

    return SessionSiteSelectors.canOwnerAccelerate(getAppStore().getState()) &&
        currentUserIsOwner(state, selectSpaceInfoItem(state));
}

export function isDataAccelerationSiteTasksSubspace(spaceInfo: SpaceInfo) {
  return isSiteTasksSpace(spaceInfo) && spaceInfo.subspaceName === 'dataAcceleration';
}

export const DEFAULT_MIN_DATA_FRESHNESS_INTERVAL_IN_MINUTES = 120;